import React, { useEffect, useState, useMemo } from "react";
import Typography from "@material-ui/core/Typography";
import styled from "styled-components";
import renderIf from "render-if";
import { Link } from "react-router-dom";
import Button from "@material-ui/core/Button";

import Page from "../layout/Page";
import ContactForm from "../components/ContactForm";
import PageHeader from "../components/PageHeader";

import useYMMTLabels from "../hooks/useYMMTLabels";
import useAccountSettings from "../hooks/useAccountSettings";
import useTradeInState from "../hooks/useTradeInState";
import submitLead from "../data/submit-lead";
import formatVehicleTitle from "../utils/format-vehicle-title";
import isError from "../utils/is-error";
import {
  fireFormInitiationEvent,
  fireFormSubmitEvent,
  fireVehicleIndicatedEvent,
  fireCompleteEvent
} from "../utils/gtm-events";

const Wrapper = styled.div`
  max-width: 750px;
  margin: 0 auto;
  display: flex;
  justify-content: center;
  flex-direction: column;
  align-items: center;
  padding: 20px;
  box-sizing: border-box;
`;

const Success = styled.div`
  margin: 0 auto;
  text-align: center;
  animation: fade-in;
  animation-duration: 2s;

  @keyframes fade-in {
    from {
      opacity: 0;
    }

    to {
      opacity: 1;
    }
  }
`;

const StyledContactForm = styled(ContactForm)`
  width: 100%;
`;

const NoValueReportPage = ({ children, match }) => {
  const { params } = match;
  const { accountId } = params;
  const [{ customDisclaimer, accountName = "dealer", removeBranding }] = useAccountSettings(
    params
  );
  const [{ leadId }] = useTradeInState();
  const [[year, make, model, trim]] = useYMMTLabels(params);
  const [complete, setComplete] = useState(false);
  const eventData = useMemo(() => year && make && model && ({ leadId, year, make, model, trim }), [leadId, year, make, model, trim]);

  const vehicleTitle =
    year && make && model ? formatVehicleTitle({ year, make, model }) : "car";

  const actionName = "Send Contact Info";

  useEffect(() => {
    if (!eventData) return;
    fireVehicleIndicatedEvent(eventData);
  }, [eventData]);

  const header = (
    <PageHeader
      title={`Sorry, we couldn't calculate your ${vehicleTitle}'s value report.`}
      subtitle={`Contact ${accountName} to schedule an appraisal to get an accurate
      trade in value.`}
      link={`/${accountId}`}
      removeBranding={removeBranding}
    />
  );

  const success = (
    <Success>
      <Typography variant="h3" color="primary">
        Contact Information Sent
      </Typography>
    </Success>
  );

  const handleOnSubmit = async (contact, actions) => {
    const { setFieldError } = actions;
    try {
      const lead = {
        contact,
        account: {
          name: accountName,
          id: accountId,
        },
        actionName,
      };

      await submitLead(lead);
      setComplete(true);
      fireFormSubmitEvent(eventData);
    } catch (err) {
      if (Array.isArray(err) && err[0].fieldName) {
          err.forEach(e => setFieldError(e.fieldName, e.message));
          return;
      }
      setComplete(err);
    }
    fireCompleteEvent(eventData);
  };

  return (
    <Page header={header} footer={children} onClick={() => fireFormInitiationEvent(eventData)}>
      <Wrapper>
        {renderIf(!complete || isError(complete))(
          <>
            <StyledContactForm
              onSubmit={handleOnSubmit}
              disclaimer={customDisclaimer}
              required
              actionName={actionName}
            />
            {renderIf(isError(complete))(
              <Typography variant="h3" color="error">
                An error occurred when sending your contact information, please
                retry.
              </Typography>
            )}
          </>
        )}
        {renderIf(complete === true)(success)}
        {renderIf(!complete || isError(complete))(
          <Typography variant="h5" color="secondary">
            OR
          </Typography>
        )}
        <Link
          to={`/${accountId}`}
          component={Button}
          variant="outlined"
          color="primary"
          style={{ margin: 10 }}
        >
          Start a new trade report
        </Link>
      </Wrapper>
    </Page>
  );
};

export default NoValueReportPage;
